import {useEffect, useState} from "react";

import {CheckOutlined, LineChartOutlined} from "@ant-design/icons";
import {Button, Statistic, Tooltip} from "antd";

import {ROP_MIN_LABEL} from "../../properties";
import {ButtonDeleteRecommendation} from "../helpers/ButtonDeleteRecommendation";
import {LockButton} from "../helpers/ButtonLockRecommendation";
import {StandardTable} from "../helpers/TablePanel";

export function RecommendationsTable({
	data,
	onApprove,
	approvalStatus,
	onForecastOpen,
	couldApprove,
	onStockAllocationOpen,
	useStockCalc,
	onLockRecommendation,
	projectId,
	material,
	loading = false,
	canEdit,
	onDeleteSuccess,
}) {
	const [tableData, setTableData] = useState(data);

	useEffect(() => {
		setTableData(data);
	}, [data]);

	const handleLockRecommendation = updatedData => {
		setTableData(updatedData);
		onLockRecommendation(updatedData);
	};

	const getColumns = () => {
		const columns = [
			{
				title: <b>#</b>,
				dataIndex: "index",
				key: "index",
				width: "5%",
				render: (text, record, index) => index + 1,
			},
			{
				title: <b>Recommendation</b>,
				dataIndex: "name",
				key: "name",
				width: "20%",
			},
			{
				title: <b>Type</b>,
				dataIndex: "type",
				key: "type",
			},
			{
				title: <b>Raw Quantity</b>,
				dataIndex: "raw",
				key: "raw",
				align: "center",
			},
			{
				title: <b>Default Apply (%)</b>,
				dataIndex: "apply",
				key: "apply",
				align: "center",
				render: apply => apply && apply * 100,
			},
			{
				title: <b>{ROP_MIN_LABEL}</b>,
				dataIndex: "min",
				key: "min",
				align: "center",
			},
			{
				title: <b>MAX</b>,
				dataIndex: "max",
				key: "max",
				align: "center",
			},
			{
				title: <b>Service factor</b>,
				dataIndex: "service_factor",
				key: "service_factor",
				align: "center",
				render: service_factor => <Statistic value={service_factor} suffix={"%"} valueStyle={{fontSize: 14}} />,
			},
			{
				title: <b>Total Cost</b>,
				dataIndex: "total_cost",
				key: "total_cost",
				align: "center",
				render: (total_cost, item) => (
					<Tooltip
						title={
							<div>
								<p>Order Cost: {item.order_cost}</p>
								<p>Holding Cost: {item.holding_cost}</p>
							</div>
						}
						placement="top">
						{total_cost}
					</Tooltip>
				),
			},
			{
				title: <b>Number of stockouts</b>,
				dataIndex: "stockouts",
				key: "stockouts",
				align: "center",
			},
			{
				title: <b>Longest Stockout (months)</b>,
				dataIndex: "longest_stockout",
				key: "longest_stockout",
				align: "center",
			},
			{
				title: <b>Actions</b>,
				dataIndex: "actions",
				key: "actions",
				align: "center",
				width: "15%",
				render: (actions, item) => (
					<div>
						<Button
							icon={
								<Tooltip title="Open forecast">
									<LineChartOutlined />
								</Tooltip>
							}
							onClick={() => onForecastOpen(item)}
							style={{margin: "0 5px"}}
						/>
						{couldApprove && !approvalStatus && (
							<Button
								icon={
									<Tooltip title="Approve recommendation">
										<CheckOutlined />
									</Tooltip>
								}
								style={{margin: "0 5px"}}
								onClick={() => onApprove(item)}
							/>
						)}
						{canEdit && (
							<LockButton
								projectId={projectId}
								material={material}
								record={item}
								lock_recommendation={item.lock_recommendation}
								onLockRecommendation={onLockRecommendation}
								data={data}
							/>
						)}
						{canEdit && item.type === "User" && (
							<ButtonDeleteRecommendation
								projectId={projectId}
								record={item}
								onDeleteSuccess={onDeleteSuccess}
							/>
						)}
					</div>
				),
			},
		];

		if (useStockCalc) {
			columns.splice(5, 0, {
				title: <b>Modified Apply(%)</b>,
				dataIndex: "apply",
				key: "apply",
				align: "center",
				render: (apply, item) => item.weight && item.weight["total"]["score"] * 100,
			});
		}

		return columns;
	};

	return (
		<div>
			<StandardTable loading={loading} tableSize={"small"} scroll={1000} data={data} columns={getColumns()} />
		</div>
	);
}
